import { clientLogger } from '@/lib/clientLogger';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpCircle, AlertTriangle } from 'lucide-react';
import { useModalLayer } from '@/lib/layerManager';
import {
  openNativeAppStore,
  type NativeUpdateDecision,
} from '@/lib/nativeAppUpdate';

type UpdateDecision = Exclude<NativeUpdateDecision, { kind: 'none' }>;

interface NativeAppUpdateModalProps {
  decision: UpdateDecision;
  onLater: () => void;
  onStoreOpened: () => void;
}

export function NativeAppUpdateModal({
  decision,
  onLater,
  onStoreOpened,
}: NativeAppUpdateModalProps) {
  const required = decision.kind === 'required';
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const layer = useModalLayer({
    enabled: true,
    onClose: required ? () => {} : onLater,
    backNavigation: !required,
  });

  const handleUpdate = async () => {
    setIsOpening(true);
    setError(null);

    try {
      await openNativeAppStore();
      if (!required) {
        onStoreOpened();
      }
    } catch (openError) {
      clientLogger.error('[Native Update] Could not open the app store:', openError);
      setError('Could not open the app store. Please try again.');
    } finally {
      setIsOpening(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      style={layer.style}
      role="dialog"
      aria-modal="true"
      aria-labelledby="native-update-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
            required ? 'bg-amber-100' : 'bg-blue-100'
          }`}
        >
          {required ? (
            <AlertTriangle className="h-7 w-7 text-amber-600" aria-hidden="true" />
          ) : (
            <ArrowUpCircle className="h-7 w-7 text-blue-600" aria-hidden="true" />
          )}
        </div>

        <div className="text-center">
          <h2 id="native-update-title" className="text-xl font-semibold text-gray-900">
            {required ? 'Update required' : 'Update available'}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {required
              ? 'Install the latest version of Service Strike to continue using the app.'
              : 'A newer version of Service Strike is available in the app store.'}
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-2">
          <button
            type="button"
            onClick={() => void handleUpdate()}
            disabled={isOpening}
            className="w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isOpening ? 'Opening app store…' : error ? 'Try again' : 'Update now'}
          </button>

          {!required && (
            <button
              type="button"
              onClick={layer.onClose}
              disabled={isOpening}
              className="w-full rounded-xl px-4 py-3 font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-60"
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
